import type { FC } from 'react'
import Icon from 'components/priority/Icon/Icon'
import { readEditorPaintedBox } from '@/utils/editor-text-box/editorTextBoxAdapter'
import type { EditorTextBoxTileTitle } from './EditorTextBoxToolbar'
import * as S from './EditorTextBoxToolbar.styled'

export type TextAlignKind = 'start' | 'center' | 'end' | 'justify'
export type TextVerticalAlignKind = 'top' | 'middle' | 'bottom'

const ALIGN_TILES: ReadonlyArray<
  EditorTextBoxTileTitle & {
    kind: TextAlignKind
    attr: 'left' | 'center' | 'right' | 'justify'
    icon:
      | 'alignLeftBarsIcon'
      | 'alignCenterBarsIcon'
      | 'alignRightBarsIcon'
      | 'alignJustifyBarsIcon'
  }
> = [
  {
    kind: 'start',
    attr: 'left',
    icon: 'alignLeftBarsIcon',
    titleKey: 'alignLeft',
    title: 'Align left',
  },
  {
    kind: 'center',
    attr: 'center',
    icon: 'alignCenterBarsIcon',
    titleKey: 'alignCenter',
    title: 'Align center',
  },
  {
    kind: 'end',
    attr: 'right',
    icon: 'alignRightBarsIcon',
    titleKey: 'alignRight',
    title: 'Align right',
  },
  {
    kind: 'justify',
    attr: 'justify',
    icon: 'alignJustifyBarsIcon',
    titleKey: 'justify',
    title: 'Justify',
  },
]

const VERTICAL_ALIGN_TILES: ReadonlyArray<
  EditorTextBoxTileTitle & {
    kind: TextVerticalAlignKind
    icon:
      | 'verticalAlignTopIcon'
      | 'verticalAlignMiddleIcon'
      | 'verticalAlignBottomIcon'
  }
> = [
  {
    kind: 'top',
    icon: 'verticalAlignTopIcon',
    titleKey: 'alignTop',
    title: 'Align top',
  },
  {
    kind: 'middle',
    icon: 'verticalAlignMiddleIcon',
    titleKey: 'alignMiddle',
    title: 'Align middle',
  },
  {
    kind: 'bottom',
    icon: 'verticalAlignBottomIcon',
    titleKey: 'alignBottom',
    title: 'Align bottom',
  },
]

/**
 * The alignment the caret's block paints. The mount exposes no block-style
 * readout, and the host's block element carries the resolved alignment as
 * its computed `text-align`, so the toolbar reads it from the DOM.
 */
export const paintedAlignAtCaret = (
  host: HTMLElement | null,
): TextAlignKind | null => {
  if (!host) return null
  const node = document.getSelection()?.focusNode ?? null
  const el = node instanceof Element ? node : node?.parentElement
  const block = el?.closest('.editor-text-block')
  if (!block || !host.contains(block)) return null
  const value = getComputedStyle(block).textAlign
  if (value === 'left') return 'start'
  if (value === 'right') return 'end'
  if (
    value === 'start' ||
    value === 'center' ||
    value === 'end' ||
    value === 'justify'
  )
    return value
  return null
}

/**
 * Where the box paints its text in its frame, through the engine's reader
 * on the editing host. It is the box's own and not a block's, so the caret
 * has no say in it.
 */
export const paintedVerticalAlign = (
  host: HTMLElement | null,
): TextVerticalAlignKind | null =>
  host ? readEditorPaintedBox(host).verticalAlign : null

type EditorTextBoxAlignMenuProps = {
  /** The alignment the caret's block paints, or null. */
  align: TextAlignKind | null
  /** Where the box's text sits in its frame, as painted, or null. */
  verticalAlign: TextVerticalAlignKind | null
  /** A tile's tooltip in the current language. */
  titleOf: (tile: EditorTextBoxTileTitle) => string
  onAlign: (align: TextAlignKind) => void
  onVerticalAlign: (verticalAlign: TextVerticalAlignKind) => void
}

/**
 * The alignment popover's two rows: the caret's paragraph set left, centered,
 * right or justified, and the box's text set at the top, the middle or the
 * bottom of its frame. The pressed tile in each row is what is painted.
 */
export const EditorTextBoxAlignMenu: FC<EditorTextBoxAlignMenuProps> = ({
  align,
  verticalAlign,
  titleOf,
  onAlign,
  onVerticalAlign,
}) => (
  <S.PillRows>
    <S.Pill data-editor-text-align-list>
      {ALIGN_TILES.map((tile) => (
        <S.Btn
          type="button"
          key={tile.kind}
          {...{ [`data-editor-text-align-${tile.attr}`]: true }}
          title={titleOf(tile)}
          pressed={align === tile.kind}
          aria-pressed={align === tile.kind}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onAlign(tile.kind)}
        >
          <Icon icon={tile.icon} width="16px" height="16px" />
        </S.Btn>
      ))}
    </S.Pill>
    <S.Pill data-editor-text-vertical-align-list>
      {VERTICAL_ALIGN_TILES.map((tile) => (
        <S.Btn
          type="button"
          key={tile.kind}
          {...{ [`data-editor-text-vertical-align-${tile.kind}`]: true }}
          title={titleOf(tile)}
          pressed={verticalAlign === tile.kind}
          aria-pressed={verticalAlign === tile.kind}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onVerticalAlign(tile.kind)}
        >
          <Icon icon={tile.icon} width="16px" height="16px" />
        </S.Btn>
      ))}
    </S.Pill>
  </S.PillRows>
)
